import React from 'react';
import SEOHead from '../../components/SEO/SEOHead';
import { Shield, Mail, Phone, Building } from 'lucide-react';

const Privacy: React.FC = () => {
  return (
    <>
      <SEOHead
        title="개인정보처리방침 - Sparrow"
        description="Sparrow 100일 개인성장 챌린지의 개인정보처리방침입니다. 사용자의 개인정보 보호를 최우선으로 합니다."
        url="https://sparrow100.pages.dev/privacy"
      />
      <div className="min-h-screen bg-gradient-to-br from-sparrow-50 via-white to-sparrow-50">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <div className="card-elevated p-6 sm:p-8 space-y-8">
            {/* 헤더 */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="p-3 bg-sparrow-100 rounded-full">
                  <Shield className="w-8 h-8 text-sparrow-600" />
                </div>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-sparrow-800">
                개인정보처리방침
              </h1>
              <p className="text-neutral-600">
                최종 수정일: 2024년 12월 18일
              </p>
            </div>

            {/* 회사 정보 */}
            <div className="bg-sparrow-50 rounded-xl p-6 space-y-4">
              <h2 className="text-xl font-bold text-sparrow-800 flex items-center gap-2">
                <Building className="w-5 h-5" />
                회사 정보
              </h2>
              <div className="space-y-2 text-neutral-700">
                <p><strong>회사명:</strong> GOLDSAJU</p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <strong>이메일:</strong> contact@goldsaju.com
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <strong>연락처:</strong> +82 010-2719-4855
                </p>
              </div>
            </div>

            {/* 본문 */}
            <div className="space-y-6 text-neutral-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">1. 개인정보의 처리 목적</h2>
                <p className="mb-4">
                  GOLDSAJU(이하 "회사")는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 개인정보보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>회원 가입 및 관리: 회원 가입의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원자격 유지·관리, 서비스 부정이용 방지, 각종 고지·통지, 고충처리 목적</li>
                  <li>서비스 제공: 100일 챌린지 진행, 활동 기록 관리, 보상 지급, 통계 및 분석 서비스 제공</li>
                  <li>마케팅 및 광고 활용: 신규 서비스 개발 및 맞춤 서비스 제공, 이벤트 및 광고성 정보 제공 및 참여기회 제공</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">2. 개인정보의 처리 및 보유기간</h2>
                <p className="mb-4">
                  회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>회원 가입 및 관리: 회원 탈퇴 시까지 (단, 관계 법령 위반에 따른 수사·조사 등이 진행중인 경우에는 해당 수사·조사 종료 시까지)</li>
                  <li>전자상거래에서의 계약·청약철회 등에 관한 기록: 5년</li>
                  <li>대금결제 및 재화 등의 공급에 관한 기록: 5년</li>
                  <li>소비자의 불만 또는 분쟁처리에 관한 기록: 3년</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">3. 처리하는 개인정보의 항목</h2>
                <p className="mb-4">회사는 다음의 개인정보 항목을 처리하고 있습니다:</p>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-bold mb-2">필수항목:</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>이메일 주소, 이름, 프로필 사진 (Google OAuth를 통한 수집)</li>
                    <li>서비스 이용 기록, 접속 로그, 쿠키, 접속 IP 정보</li>
                  </ul>
                  <h3 className="font-bold mt-4 mb-2">선택항목:</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>알림 설정, 활동 선호도</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">4. 개인정보의 제3자 제공</h2>
                <p>
                  회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 개인정보보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">5. 개인정보처리의 위탁</h2>
                <p className="mb-4">
                  회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다:
                </p>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Cloudflare:</strong> 서비스 호스팅 및 데이터베이스 관리</li>
                    <li><strong>Google:</strong> OAuth 인증 서비스</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">6. 정보주체의 권리·의무 및 행사방법</h2>
                <p className="mb-4">
                  정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>개인정보 처리정지 요구권</li>
                  <li>개인정보 열람요구권</li>
                  <li>개인정보 정정·삭제요구권</li>
                  <li>개인정보 처리정지 요구권</li>
                </ul>
                <p className="mt-4">
                  권리 행사는 회사에 대해 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며 회사는 이에 대해 지체 없이 조치하겠습니다.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">7. 개인정보의 파기</h2>
                <p className="mb-4">
                  회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.
                </p>
                <p>
                  파기의 절차 및 방법은 다음과 같습니다:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                  <li><strong>파기절차:</strong> 회사는 파기 사유가 발생한 개인정보를 선정하고, 회사의 개인정보 보호책임자의 승인을 받아 개인정보를 파기합니다.</li>
                  <li><strong>파기방법:</strong> 전자적 파일 형태의 정보는 기록을 재생할 수 없는 기술적 방법을 사용합니다.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">8. 개인정보 보호책임자</h2>
                <p className="mb-4">
                  회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
                </p>
                <div className="bg-sparrow-50 rounded-lg p-6">
                  <p className="font-bold mb-2">개인정보 보호책임자</p>
                  <ul className="space-y-2">
                    <li><strong>회사명:</strong> GOLDSAJU</li>
                    <li><strong>이메일:</strong> contact@goldsaju.com</li>
                    <li><strong>연락처:</strong> +82 010-2719-4855</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-sparrow-800 mb-4">9. 개인정보 처리방침 변경</h2>
                <p>
                  이 개인정보처리방침은 2024년 12월 18일부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.
                </p>
              </section>
            </div>

            {/* 문의 */}
            <div className="border-t border-neutral-200 pt-6 mt-8">
              <p className="text-center text-neutral-600">
                개인정보 처리방침에 대한 문의사항이 있으시면 언제든지 연락주시기 바랍니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
